import { CompositeDisposable, type Disposable } from '@gitlab-org/disposable';
import type { Transport } from '@gitlab-org/webview-transport';
import type { WebviewInstanceId } from '@gitlab-org/webview-plugin';
import { Logger } from '@gitlab-org/logging';
import type { WebviewRuntimeMessageBus } from '../../events';
import { createIsManagedFunc, setupEventSubscriptions, setupTransportEventHandlers } from './utils';

const setupTransport = (
  transport: Transport,
  runtimeMessageBus: WebviewRuntimeMessageBus,
  logger: Logger,
): Disposable => {
  logger.debug(`Setting up transport: ${transport.constructor.name}`);
  const managedInstances = new Set<WebviewInstanceId>();
  const isManaged = createIsManagedFunc(managedInstances);

  const disposable = new CompositeDisposable();

  disposable.add(
    ...setupTransportEventHandlers(transport, runtimeMessageBus, managedInstances),
    ...setupEventSubscriptions(runtimeMessageBus, transport, isManaged),
  );

  logger.debug(`Transport ${transport.constructor.name} setup completed`);

  return {
    dispose: () => {
      logger.debug(`Disposing transport: ${transport.constructor.name}`);
      disposable.dispose();
      logger.debug(`Transport ${transport.constructor.name} disposed`);
    },
  };
};

export const setupTransports = (
  transports: Transport[],
  runtimeMessageBus: WebviewRuntimeMessageBus,
  logger: Logger,
): Disposable => {
  logger.debug(`Setting up ${transports.length} transports`);
  const disposables = transports.map((transport) =>
    setupTransport(transport, runtimeMessageBus, logger),
  );

  return {
    dispose: () => {
      logger.debug(`Disposing ${disposables.length} transports`);
      for (const disposable of disposables) {
        disposable.dispose();
      }
      logger.debug('All transports disposed');
    },
  };
};
